import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi, type MeResponse, type Ticket } from '@/lib/api';
import { useMe } from '@/lib/useMe';
import { MeGate } from '@/components/MeGate';
import { TicketList } from '@/components/TicketList';
import { MessageThread } from '@/components/MessageThread';
import { ComposeBar } from '@/components/ComposeBar';
import { AgentActivityTimeline } from '@/components/AgentActivityTimeline';
import { SetupChecklist } from '@/components/SetupChecklist';
import { useTicketAgent } from '@/lib/useTicketAgent';
import type { TicketState, StoredMessage } from '../../../src/types';
import { CoreIcon } from '@/components/CoreIcon';

const NOTIFICATION_SOUND_URI = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVoGAACAgICAgICAgICAgIB/f39/f4CAgIGBgYKCgoODg4SEhIWFhYWFhYWFhYWFhYWFhYSEhIODg4KCgoGBgYCAgH9/f35+fn5+fn5+f3+AgIGBgoKDg4SEhYaGh4eHh4eHh4eHhoeGhYWEg4OCgYGAf39+fn19fX19fX1+fn9/gIGCg4OEhYaHiImJiYmJiYmJiIiHhoWEg4KBgH9+fX19fHx8fHx8fX1+f4CBgoOEhYeIiYqKioqKioqKiYiHhoWEgoGAf359fHx8e3t7e3t8fH1+f4CCg4WGh4iJioqLi4uLi4qJiIeGhYOCgH9+fXx7e3t7e3t7e3x9fn+AgoOFhoeIiYqLi4uLi4uKiYiHhYSDgYB+fXx7e3p6enp6ent8fX5/gYKEhYeIiYqLjIyMjIuLioiHhoSDgYB+fXx7enp6enp6ent8fX+AgYOEhoiJiouMjIyMjIuKiYeGhIOBgH5+fHt6enp6enp6e3x9f4CBg4WGiImKi4yMjIyMi4qJh4aEg4GAf319fHt7e3t7e3t7fH1+gIGDhYaHiYqLi4yMjIuLiomHhoSDgYB/fn18e3t7e3t7e3t8fX5/gYKEhYeIiYqLi4uLi4uKiYiHhYSDgYB/fn18e3t7e3t7e3x8fX5/gIKDhYaHiImKi4uLi4uKiYiHhoWDgoCAf359fHx7e3t7e3t8fH1+f4CBgoSFhoeIiYqKioqKiomIh4aFhIOCgYB/fn19fHx8fHx8fH19fn9/gYGCg4SEhYaGhoeHh4eHh4eHhoaFhISEg4OCgoGBgICAf39/fn5+fn5+fn5+f3+AgICBgYGCgoKDg4ODhISEhISEhISEhISEhISEhIODg4ODg4KCgoGBgYCAgIB/f39/f39/f39/f3+AgICAgICAgICAgA==';

function playNotificationSound() {
  try {
    const audio = new Audio(NOTIFICATION_SOUND_URI);
    audio.volume = 0.4;
    void audio.play().catch(() => {});
  } catch {}
}

type FilterKey = 'all' | 'awaiting_approval' | 'open' | 'resolved' | 'spam';

const STATUS_FILTERS: Array<{ key: FilterKey; label: string; hint?: string }> = [
  { key: 'awaiting_approval', label: 'Needs review', hint: 'AI drafted a reply' },
  { key: 'all',               label: 'All' },
  { key: 'open',              label: 'Open' },
  { key: 'resolved',          label: 'Resolved' },
  { key: 'spam',              label: 'Spam' },
];

const APP_DOMAIN = 'edgemail.cloud';

export function Inbox() {
  const meStatus = useMe();
  return <MeGate status={meStatus}>{(me) => <InboxBody me={me} />}</MeGate>;
}

function InboxBody({ me }: { me: MeResponse }) {
  const orgId = me.org!.id;
  const orgSlug = me.org!.slug;
  const api = useApi();
  const nav = useNavigate();
  const { ticketId } = useParams<{ ticketId?: string }>();
  const [allTickets, setAllTickets] = useState<Ticket[]>([]);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [query, setQuery] = useState('');
  const [loadingList, setLoadingList] = useState(true);

  const fetchTickets = useCallback(() => {
    return api
      .get<{ tickets: Ticket[] }>(`/api/orgs/${orgId}/tickets`)
      .then((r) => setAllTickets(r.tickets))
      .catch((err) => console.error(err));
  }, [api, orgId]);

  useEffect(() => {
    setLoadingList(true);
    void fetchTickets().finally(() => setLoadingList(false));
    const interval = setInterval(fetchTickets, 15_000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  // Local counts for the filter rail — no extra round-trips.
  const counts = useMemo(() => {
    const c: Record<FilterKey, number> = {
      all: allTickets.length,
      awaiting_approval: 0,
      open: 0,
      resolved: 0,
      spam: 0,
    };
    for (const t of allTickets) c[t.status as FilterKey] = (c[t.status as FilterKey] ?? 0) + 1;
    return c;
  }, [allTickets]);

  const tickets = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTickets.filter((t) => {
      if (filter !== 'all' && t.status !== filter) return false;
      if (!q) return true;
      const hay = `${t.subject ?? ''} ${t.from_email} ${t.ai_category ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allTickets, filter, query]);

  // First-run: no tickets at all → setup checklist fills the main area.
  const showChecklist = !loadingList && allTickets.length === 0;

  const selected = useMemo(
    () => allTickets.find((t) => t.id === ticketId) ?? null,
    [allTickets, ticketId],
  );
  const selectedBareTicketId = selected
    ? selected.id.startsWith(`${orgId}--`) ? selected.id.slice((orgId ?? '').length + 2) : selected.id
    : null;

  const [live, setLive] = useState<TicketState | null>(null);
  const prevLastMsgRef = useRef(0);
  const onLiveState = useCallback((s: TicketState) => {
    setLive(s);
    if (s.lastMessageAt && s.lastMessageAt > prevLastMsgRef.current && prevLastMsgRef.current > 0) {
      playNotificationSound();
      void fetchTickets();
    }
    prevLastMsgRef.current = s.lastMessageAt ?? 0;
  }, [fetchTickets]);
  const agent = useTicketAgent(orgId, selectedBareTicketId, onLiveState);
  const [history, setHistory] = useState<StoredMessage[] | null>(null);
  const historyTicketRef = useRef<string | null>(null);

  const liveLastMessageAt = live?.lastMessageAt ?? 0;
  useEffect(() => {
    if (!selected) { setHistory(null); setLive(null); historyTicketRef.current = null; return; }
    // Show loading skeleton when switching to a different ticket
    if (historyTicketRef.current !== selected.id) {
      setHistory(null);
      historyTicketRef.current = selected.id;
    }
    let cancelled = false;
    const load = async () => {
      try {
        await agent.ready;
        if (cancelled) return;
        const msgs = await agent.call('loadHistory', [50]) as StoredMessage[];
        if (!cancelled) setHistory(msgs);
      } catch (err) {
        console.error('loadHistory failed', err);
        if (!cancelled) setHistory([]);
      }
    };
    void load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, liveLastMessageAt]);

  // Keep the ticket list in sync with the DO's own state so the status badge
  // and category update the instant the operator hits Send (previously the
  // list stayed stale until the page was reloaded).
  useEffect(() => {
    if (!selected || !live) return;
    setAllTickets((prev) => prev.map((t) =>
      t.id === selected.id
        ? {
            ...t,
            status: live.status ?? t.status,
            ai_category: live.category ?? t.ai_category,
            priority: live.priority ?? t.priority,
            last_activity: live.lastMessageAt ?? t.last_activity,
          }
        : t,
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, live?.status, live?.category, live?.priority, live?.lastMessageAt]);

  if (showChecklist) {
    return <SetupChecklist orgId={orgId} orgSlug={orgSlug} orgName={me.org!.name} />;
  }

  return (
    <div className="h-full grid grid-cols-[260px_360px_minmax(0,1fr)] bg-background">
      {/* ── Filter rail ──────────────────────────────────────────────── */}
      <FilterRail
        orgSlug={orgSlug}
        emailPrefix={me.org!.email_prefix}
        counts={counts}
        active={filter}
        onChange={setFilter}
      />

      {/* ── Ticket list ─────────────────────────────────────────────── */}
      <section className="border-r border-border bg-surface flex flex-col min-h-0">
        <header className="px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between">
            <h1 className="text-[15px] font-semibold text-foreground">
              {STATUS_FILTERS.find((f) => f.key === filter)?.label ?? 'Inbox'}
            </h1>
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {tickets.length} of {allTickets.length}
            </span>
          </div>
          <div className="mt-2.5 relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
              <CoreIcon name="search" size={14} />
            </span>
            <input
              type="search"
              placeholder="Search conversations…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="input !h-8 !pl-8 !text-[13px]"
              aria-label="Search conversations"
            />
          </div>
        </header>
        <TicketList
          tickets={tickets}
          loading={loadingList}
          selectedId={selected?.id ?? null}
          onSelect={(t) => nav(`/app/inbox/${encodeURIComponent(t.id)}`)}
        />
      </section>

      {/* ── Conversation + compose + context drawer ─────────────────── */}
      <section className="flex min-w-0 min-h-0">
        {selected ? (
          <>
            <div className="flex-1 flex flex-col min-w-0 min-h-0">
              <MessageThread
                ticket={selected}
                messages={history ?? []}
                liveState={live}
                loading={history === null}
                onMarkResolved={() => agent.call('markResolved', [])}
              />
              {live && (
                <ComposeBar
                  ticket={selected}
                  state={live}
                  onApprove={(id, body) => agent.call('approveDraft', [id, body])}
                  onReject={(id, reason) => agent.call('rejectDraft', [id, reason])}
                  onRegenerate={() => agent.call('regenerateDraft', [])}
                  onManualReply={(body) => agent.call('sendManualReply', [body])}
                  onMarkSpam={() => agent.call('markSpam', [])}
                />
              )}
            </div>
            <ContextDrawer ticket={selected} state={live} />
          </>
        ) : (
          <EmptyPane />
        )}
      </section>
    </div>
  );
}

function FilterRail({
  orgSlug,
  emailPrefix,
  counts,
  active,
  onChange,
}: {
  orgSlug: string;
  emailPrefix?: string | null;
  counts: Record<FilterKey, number>;
  active: FilterKey;
  onChange: (k: FilterKey) => void;
}) {
  const [copied, setCopied] = useState(false);
  const orgEmail = `${emailPrefix || orgSlug}@${APP_DOMAIN}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(orgEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <aside className="border-r border-border flex flex-col min-h-0">
      <div className="px-4 pt-4 pb-3">
        <div className="mono-label mb-2">Inbox</div>
        <h2 className="text-[17px] font-semibold tracking-sub leading-heading">
          Conversations
        </h2>
      </div>

      <nav className="px-2">
        <ul className="space-y-0.5">
          {STATUS_FILTERS.map((f) => {
            const isActive = active === f.key;
            return (
              <li key={f.key}>
                <button
                  type="button"
                  onClick={() => onChange(f.key)}
                  aria-current={isActive ? 'page' : undefined}
                  className={
                    'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13.5px] transition-colors ' +
                    (isActive
                      ? 'bg-foreground text-surface'
                      : 'text-foreground hover:bg-muted')
                  }
                >
                  {f.key === 'awaiting_approval' && (
                    <span className="text-accent"><CoreIcon name="sparkles" size={14} /></span>
                  )}
                  {f.key === 'all'      && <span className="opacity-70"><CoreIcon name="inbox" size={14} /></span>}
                  {f.key === 'open'     && <span className="opacity-70"><CoreIcon name="chart-line" size={14} /></span>}
                  {f.key === 'resolved' && <span className="opacity-70"><CoreIcon name="check-alt" size={14} /></span>}
                  {f.key === 'spam'     && <span className="opacity-70"><CoreIcon name="warning" size={14} /></span>}
                  <span className="flex-1 text-left truncate">{f.label}</span>
                  <span className={
                    'text-[11px] tabular-nums ' +
                    (isActive ? 'text-surface/75' : 'text-muted-foreground')
                  }>
                    {counts[f.key] ?? 0}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mt-6 px-4">
        <div className="mono-label mb-2">Channels</div>
        <div className="rounded-lg border border-border bg-surface p-3">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1">
            <CoreIcon name="at" size={12} />
            Your inbound address
          </div>
          <button
            type="button"
            onClick={copy}
            aria-label="Copy inbound email address"
            className="w-full text-left flex items-center gap-2 rounded-sm border border-border bg-background px-2 py-1.5 hover:border-foreground transition-colors group"
          >
            <span className="text-[12px] font-medium text-foreground truncate">
              {orgEmail}
            </span>
            {copied
              ? <span className="text-accent ml-auto shrink-0"><CoreIcon name="check-alt" size={14} /></span>
              : <span className="text-muted-foreground ml-auto shrink-0 group-hover:text-foreground"><CoreIcon name="copy" size={14} /></span>
            }
          </button>
          <p className="text-[11px] text-muted-foreground mt-2 leading-[1.4]">
            Anyone who emails this address opens a ticket. Fin AI drafts a reply; you approve before it sends.
          </p>
        </div>
      </div>

      <div className="mt-auto px-4 py-3 border-t border-border">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="text-accent"><CoreIcon name="sparkles" size={12} /></span>
          Human-in-the-loop enabled
        </div>
      </div>
    </aside>
  );
}

interface CheckpointEntry {
  id: string;
  step: number;
  node: string;
  status: string;
  duration_ms: number | null;
  output_json: string | null;
  created_at: number;
}

function ContextDrawer({ ticket, state }: { ticket: Ticket; state: TicketState | null }) {
  const api = useApi();
  const confidence = state?.currentDraft?.confidence ?? ticket.ai_confidence ?? null;
  const activity = state?.toolActivity ?? [];
  const [checkpoints, setCheckpoints] = useState<CheckpointEntry[]>([]);
  const [checkpointsLoading, setCheckpointsLoading] = useState(true);
  const [memory, setMemory] = useState<{ facts: Array<{ key: string; value: string }>; summary: string | null; sentiment: string | null } | null>(null);
  const [memoryLoading, setMemoryLoading] = useState(true);
  const meStatus = useMe();
  const orgId = meStatus.state === 'ok' ? meStatus.me.org?.id : null;

  useEffect(() => {
    setCheckpointsLoading(true);
    api.get<{ checkpoints: CheckpointEntry[] }>(`/api/tickets/${encodeURIComponent(ticket.id)}/checkpoints`)
      .then((r) => {
        const seen = new Map<string, CheckpointEntry>();
        for (const cp of r.checkpoints) {
          const existing = seen.get(cp.node);
          if (!existing || cp.created_at > existing.created_at) {
            seen.set(cp.node, cp);
          }
        }
        setCheckpoints(Array.from(seen.values()).sort((a, b) => a.step - b.step));
      })
      .catch(() => {})
      .finally(() => setCheckpointsLoading(false));
  }, [api, ticket.id]);

  useEffect(() => {
    if (!orgId) return;
    setMemoryLoading(true);
    api.get<{ memory: typeof memory }>(`/api/orgs/${orgId}/customers/${encodeURIComponent(ticket.from_email)}/memory`)
      .then((r) => setMemory(r.memory))
      .catch(() => {})
      .finally(() => setMemoryLoading(false));
  }, [api, orgId, ticket.from_email]);

  return (
    <aside className="hidden lg:flex flex-col w-[280px] border-l border-border bg-surface min-h-0 overflow-y-auto">
      <div className="p-4 space-y-4">
        <div>
          <div className="mono-label mb-2">Customer</div>
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="text-[13px] font-medium text-foreground truncate">
              {ticket.from_email}
            </div>
            <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[11px]">
              <span className="text-muted-foreground">Priority</span>
              <span className="text-foreground capitalize">{ticket.priority ?? 'normal'}</span>
              <span className="text-muted-foreground">Category</span>
              <span className="text-foreground">{ticket.ai_category ?? '—'}</span>
              <span className="text-muted-foreground">Confidence</span>
              <span className="text-foreground tabular-nums">
                {confidence !== null ? `${Math.round(confidence * 100)}%` : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Customer Memory */}
        {memoryLoading ? (
          <div>
            <div className="mono-label mb-2 flex items-center gap-1.5">
              <CoreIcon name="lightbulb" size={12} />
              Memory
            </div>
            <div className="rounded-lg border border-border bg-background p-3 space-y-2 animate-pulse">
              <div className="h-2.5 w-24 bg-muted rounded-sm" />
              <div className="h-2.5 w-full bg-muted rounded-sm" />
              <div className="h-2.5 w-3/4 bg-muted rounded-sm" />
            </div>
          </div>
        ) : memory ? (
          <div>
            <div className="mono-label mb-2 flex items-center gap-1.5">
              <CoreIcon name="lightbulb" size={12} />
              Memory
            </div>
            <div className="rounded-lg border border-border bg-background p-3 space-y-2">
              {memory.sentiment && (
                <div className="flex items-center gap-1.5 text-[11px]">
                  <span className="text-muted-foreground">Sentiment:</span>
                  <span className="capitalize">{memory.sentiment}</span>
                </div>
              )}
              {memory.summary && (
                <p className="text-[11px] text-muted-foreground">{memory.summary}</p>
              )}
              {memory.facts?.length > 0 && (
                <div className="space-y-1">
                  {memory.facts.slice(0, 5).map((f, i) => (
                    <div key={i} className="text-[11px]">
                      <span className="text-muted-foreground">{f.key}:</span>{' '}
                      <span>{f.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}

        <div>
          <div className="mono-label mb-2 flex items-center gap-1.5">
            <CoreIcon name="tags" size={12} />
            Tags
          </div>
          <div className="flex flex-wrap gap-1">
            {(state?.tags ?? []).length === 0 ? (
              <span className="text-[11px] text-muted-foreground">None yet.</span>
            ) : (
              state!.tags.map((t) => (
                <span key={t} className="inline-flex rounded-sm border border-border bg-background px-1.5 py-0.5 text-[10.5px] uppercase tracking-mono text-muted-foreground">
                  {t}
                </span>
              ))
            )}
          </div>
        </div>

        <div>
          <div className="mono-label mb-2 flex items-center gap-1.5">
            <CoreIcon name="cog" size={12} />
            Agent activity
          </div>
          {activity.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">No tool calls yet.</p>
          ) : (
            <AgentActivityTimeline items={activity} />
          )}
        </div>

        {/* Time Travel — Agent reasoning steps */}
        <div>
          <div className="mono-label mb-2 flex items-center gap-1.5">
            <CoreIcon name="history" size={12} />
            Reasoning steps
          </div>
          {checkpointsLoading ? (
            <div className="space-y-2 animate-pulse">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-2 py-1.5">
                  <div className="h-2 w-2 rounded-full bg-muted shrink-0" />
                  <div className="h-2.5 bg-muted rounded-sm" style={{ width: `${60 + i * 15}%` }} />
                </div>
              ))}
            </div>
          ) : checkpoints.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">No reasoning data yet.</p>
          ) : (
            <div className="space-y-0.5">
              {checkpoints.map((cp) => (
                <div key={`${cp.step}-${cp.node}`} className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
                  <span
                    className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${
                      cp.status === 'completed' ? 'bg-success'
                      : cp.status === 'interrupted' ? 'bg-warning'
                      : cp.status === 'error' ? 'bg-destructive'
                      : 'bg-muted-foreground animate-pulse'
                    }`}
                    aria-label={`Step ${cp.step}: ${cp.status}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-medium capitalize">
                      {cp.node.replaceAll('_', ' ')}
                    </div>
                    {cp.duration_ms != null && (
                      <span className="text-[10px] text-muted-foreground tabular-nums">
                        {cp.duration_ms >= 1000 ? `${(cp.duration_ms / 1000).toFixed(1)}s` : `${cp.duration_ms}ms`}
                      </span>
                    )}
                  </div>
                  <span className={`text-[10px] capitalize shrink-0 ${
                    cp.status === 'completed' ? 'text-success-foreground'
                    : cp.status === 'interrupted' ? 'text-warning'
                    : cp.status === 'error' ? 'text-destructive'
                    : 'text-muted-foreground'
                  }`}>{cp.status === 'interrupted' ? 'awaiting review' : cp.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function EmptyPane() {
  return (
    <div className="grid place-items-center w-full h-full text-muted-foreground">
      <div className="text-center max-w-sm p-8">
        <div className="mx-auto mb-4 h-12 w-12 rounded-lg border border-border bg-surface grid place-items-center text-muted-foreground">
          <CoreIcon name="inbox" size={20} />
        </div>
        <h3 className="text-foreground font-medium text-[15px]">Select a conversation</h3>
        <p className="text-sm mt-1">
          Pick a ticket on the left to review the Fin-drafted reply and send when ready.
        </p>
      </div>
    </div>
  );
}

